import React, { ReactElement, ReactNode, useContext } from 'react'
import { useTranslation } from 'react-i18next'
import FlexRow from 'components/Layout/FlexRow'
import FlexCol from 'components/Layout/FlexCol'
import Button, { ButtonSize, ButtonTheme } from 'components/Form/Button'
import { Routes } from 'routes'
import TsxUtils from 'utils/tsx'
import ArrayUtils from 'utils/array'
import { RowBody } from './types'
import { AppContext } from 'contexts/AppContext'
import { CAMPAIGN_ACTION } from 'constant/authorization'
import i18n from 'i18n'
import './VoohCampaignSummary.scss'
import { VoohCampaignForm } from '../../VoohCampaignForm/types'
import DateUtils from '../../../../utils/date'
import { DATE_FORMAT } from '../../../../constant'
import { VoohCampaign } from '../../../../types/campaign'

const VoohCampaignSummary: React.FC<{ voohForm: VoohCampaignForm }> = ({ voohForm }) => {
  const { t } = useTranslation()
  const { allowedFor } = useContext(AppContext)

  const campaignTerm = (start: VoohCampaign['startDate'], end: VoohCampaign['endDate']): string =>
    `${DateUtils.parseAndFormat(start, DATE_FORMAT)} - ${DateUtils.parseAndFormat(
      end,
      DATE_FORMAT
    )}`

  const row = (a: RowBody, b: RowBody, key: number): ReactElement => (
    <FlexRow
      key={key}
      className={
        'CampaignDetailsSummary__row' +
        TsxUtils.extraStyle(!a || !b, 'CampaignDetailsSummary__row--half')
      }
    >
      {a && rowInside(a)}
      {b && rowInside(b)}
    </FlexRow>
  )

  const rowInside = (el: RowBody) => (
    <FlexRow className='CampaignDetailsSummary__row--inside'>
      <>
        <FlexCol className='CampaignDetailsSummary__col  CampaignDetailsSummary__title'>
          {el.title}
        </FlexCol>
        <FlexCol
          className={
            'CampaignDetailsSummary__col' +
            TsxUtils.extraStyle(el.valueExtraStyle, el.valueExtraStyle)
          }
        >
          {el.value}
        </FlexCol>
      </>
    </FlexRow>
  )

  const briefName = (): RowBody => ({
    title: t('common.briefName'),
    value: voohForm.baseValues.briefName,
  })

  const agencyName = (): RowBody => ({
    title: t('common.agency'),
    value: voohForm.baseValues.agency?.label,
  })

  const supervisors = (): RowBody => ({
    title: t('common.supervisors'),
    value:
      voohForm.baseValues.supervisors.length > 0
        ? voohForm.baseValues.supervisors
            .map(supervisor => supervisor.label)
            .sort()
            .join(', ')
        : '-',
  })

  const emissionTime = (): RowBody => ({
    title: t('common.campaignTerm'),
    value: campaignTerm(voohForm.baseValues.startDate, voohForm.baseValues.endDate),
  })

  const mediaType = (): RowBody => ({
    title: t('common.mediaType'),
    value: t(`mediaType.${voohForm.detailsValues.mediaFormat}`),
  })

  const usedMedia = (): RowBody => ({
    title: t('common.usedMedia'),
    value: voohForm.selectedMediums.length,
  })

  const offer = (): RowBody => ({
    title: t('common.offerType'),
    value: voohForm.detailsValues.offer
      ? i18n.t(`offer.${voohForm.detailsValues.offer}`).toString()
      : '-',
  })

  const campaignId = (): RowBody => ({
    title: t('common.campaignId'),
    value: `${voohForm.id}`,
  })

  const mediaDuration = (): RowBody => ({
    title: t('common.mediaDuration'),
    value: `${
      voohForm.detailsValues.mediaDuration
        ? voohForm.detailsValues.mediaDuration
        : t('form.campaign.creations.open')
    }`,
  })

  const elements: (RowBody | null)[] = [
    briefName(),
    agencyName(),
    supervisors(),
    emissionTime(),
    mediaType(),
    usedMedia(),
    offer(),
    ...(allowedFor({ template: CAMPAIGN_ACTION.CAMPAIGN_ID }) ? [campaignId()] : []),
    mediaDuration(),
  ]

  const Buttons = (): ReactNode => (
    <div className='CampaignDetailsSummary__buttons'>
      <Button
        theme={ButtonTheme.NAVY_BLUE_OUTLINE}
        size={ButtonSize.SMALL}
        toUrl={Routes.VOOH_CAMPAIGNS.EDIT(voohForm.id)} // eslint-disable-line react/prop-types
      >
        {t('common.edit')}
      </Button>
    </div>
  )

  return (
    <>
      {ArrayUtils.toChunks(
        elements.filter(el => !!el),
        2
      ).map(([a, b], key) => row(a as RowBody, b as RowBody, key))}
      {Buttons()}
    </>
  )
}

export default VoohCampaignSummary
